import * as RadixAvatar from '@radix-ui/react-avatar'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn.js'

const avatar = cva(
  'relative flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-medium',
  {
    variants: {
      size: {
        sm: 'size-8 text-caption',
        md: 'size-10 text-second',
        lg: 'size-14 text-body',
        /* The kit's `--xl`, 72px, for the portrait at the top of a record. */
        xl: 'size-18 text-title',
      },
      tone: {
        neutral: 'bg-sunk text-ink-2',
        teal: 'bg-teal-soft text-teal-text',
        water: 'bg-water-soft text-water-text',
        coral: 'bg-coral-soft text-coral-text',
      },
    },
    defaultVariants: { size: 'md', tone: 'neutral' },
  },
)

export interface AvatarProps extends VariantProps<typeof avatar> {
  /** The full name. Initials are taken from it, so nobody computes them twice. */
  name: string
  src?: string | null
  className?: string
}

/** Initials from a name: the first letter of the first and last words, at most two. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '·'
  const first = words[0]![0] ?? ''
  const last = words.length > 1 ? (words[words.length - 1]![0] ?? '') : ''
  return (first + last).toUpperCase()
}

/**
 * A face, or the initials standing in for one.
 *
 * Never an empty circle: a person with no photograph still has a name, and a blank
 * disc in a list of people reads as a row that failed to load.
 */
export function Avatar({ name, src, size, tone, className }: AvatarProps) {
  return (
    <RadixAvatar.Root className={cn(avatar({ size, tone }), className)}>
      {src && <RadixAvatar.Image src={src} alt="" className="size-full object-cover" />}
      <RadixAvatar.Fallback delayMs={src ? 300 : 0} aria-hidden>
        {initialsOf(name)}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  )
}
